/**
 * Client-only session + page-view identifiers for the Customer Intelligence
 * tracker. SSR-safe (returns empty strings on the server).
 *
 * A "session" ends after 30 minutes of inactivity or when a new UTM campaign
 * arrives on a fresh landing. A "page view" is one route render; SPA
 * navigations reset it via `resetPageView()`.
 */
const SESSION_KEY = "tw_sid";
const SESSION_META_KEY = "tw_sid_meta";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "id_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

type SessionMeta = {
  sessionId: string;
  startedAt: number;
  lastActivityAt: number;
  landingPage: string;
  referrer: string;
  utm: Record<string, string | null>;
};

function parseUtm(): Record<string, string | null> {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
  const out: Record<string, string | null> = {};
  for (const k of keys) out[k] = p.get(k);
  return out;
}

function readMeta(): SessionMeta | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(SESSION_META_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SessionMeta;
  } catch {
    return null;
  }
}

function writeMeta(meta: SessionMeta) {
  try {
    sessionStorage.setItem(SESSION_META_KEY, JSON.stringify(meta));
    sessionStorage.setItem(SESSION_KEY, meta.sessionId);
  } catch {
    /* private mode */
  }
}

function newMeta(): SessionMeta {
  const now = Date.now();
  return {
    sessionId: uuid(),
    startedAt: now,
    lastActivityAt: now,
    landingPage: typeof window === "undefined" ? "" : window.location.pathname,
    referrer: typeof document === "undefined" ? "" : document.referrer || "",
    utm: parseUtm(),
  };
}

/** Get (or create) the active session, rolling on 30-min inactivity. */
export function getSession(): SessionMeta {
  if (typeof window === "undefined") return newMeta();
  const now = Date.now();
  const meta = readMeta();
  const incomingUtm = parseUtm();
  const newCampaign = Object.values(incomingUtm).some(Boolean) &&
    Object.keys(incomingUtm).some(key => incomingUtm[key] !== (meta?.utm[key] ?? null));
  if (meta && now - meta.lastActivityAt < SESSION_TIMEOUT_MS && !newCampaign) {
    meta.lastActivityAt = now;
    writeMeta(meta);
    return meta;
  }
  const fresh = newMeta();
  writeMeta(fresh);
  return fresh;
}

export function touchSession(): void {
  const meta = readMeta();
  if (!meta) {
    getSession();
    return;
  }
  const now = Date.now();
  if (now - meta.lastActivityAt >= SESSION_TIMEOUT_MS) {
    writeMeta(newMeta());
    return;
  }
  meta.lastActivityAt = now;
  writeMeta(meta);
}

// ---- page view --------------------------------------------------------
let currentPageViewId = "";
let currentPagePath = "";

export function resetPageView(path: string): string {
  currentPageViewId = uuid();
  currentPagePath = path;
  return currentPageViewId;
}

export function getPageViewId(): string {
  if (!currentPageViewId) resetPageView(typeof window === "undefined" ? "" : window.location.pathname);
  return currentPageViewId;
}

export function getPagePath(): string {
  return currentPagePath || (typeof window === "undefined" ? "" : window.location.pathname);
}

export function newEventId(): string {
  return uuid();
}
